import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

// Request types
export interface DecklistCardInput {
  name: string;
  setCode?: string;
  quantity?: number;
}

export interface BatchResolveRequest {
  cards: DecklistCardInput[];
  includePrice?: boolean;
}

// Response types
export interface ResolvedCard {
  requestedName: string;
  quantity: number;
  scryfallID: string;
  name: string;
  typeLine: string;
  manaCost: string | null;
  setCode: string;
  setName: string;
  imageUris: {
    normal?: string;
    small?: string;
    art_crop?: string;
  };
  price: {
    usd: number | null;
    usdFoil: number | null;
  } | null;
  rarity: string;
  colors: string[] | null;
}

export interface UnresolvedCard {
  requestedName: string;
  requestedSet?: string;
  quantity: number;
  reason: 'NOT_FOUND' | 'SET_NOT_FOUND' | 'NOT_IN_SET';
  message: string;
}

export interface BatchResolveResponse {
  resolved: ResolvedCard[];
  unresolved: UnresolvedCard[];
  stats: {
    total: number;
    resolvedCount: number;
    unresolvedCount: number;
  };
}

// Card type categories for decklist organization
export type CardTypeCategory =
  | 'Creature'
  | 'Planeswalker'
  | 'Instant'
  | 'Sorcery'
  | 'Enchantment'
  | 'Artifact'
  | 'Battle'
  | 'Land'
  | 'Other';

/**
 * Categorize a card by its typeLine using priority order from spec:
 * Creature > Planeswalker > Instant > Sorcery > Enchantment > Artifact > Battle > Land > Other
 * Multi-type cards use first matching type (e.g., "Artifact Creature" → Creature)
 */
export function getCardCategory(typeLine: string | null): CardTypeCategory {
  if (!typeLine) return 'Other';

  const line = typeLine.toLowerCase();

  // Check in priority order
  if (line.includes('creature')) return 'Creature';
  if (line.includes('planeswalker')) return 'Planeswalker';
  if (line.includes('instant')) return 'Instant';
  if (line.includes('sorcery')) return 'Sorcery';
  if (line.includes('enchantment')) return 'Enchantment';
  if (line.includes('artifact')) return 'Artifact';
  if (line.includes('battle')) return 'Battle';
  if (line.includes('land')) return 'Land';

  return 'Other';
}

/**
 * Category display order for decklist sections
 */
export const CATEGORY_ORDER: CardTypeCategory[] = [
  'Creature',
  'Planeswalker',
  'Instant',
  'Sorcery',
  'Enchantment',
  'Artifact',
  'Battle',
  'Land',
  'Other',
];

/**
 * Parse decklist text into card entries
 * Supports formats:
 * - "4 Lightning Bolt"
 * - "4x Lightning Bolt"
 * - "Lightning Bolt" (defaults to qty 1)
 * - "4 Lightning Bolt [M10]" (with set code)
 */
export function parseDecklistText(text: string): DecklistCardInput[] {
  const lines = text.split('\n').filter((line) => line.trim());
  const cards: DecklistCardInput[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip section headers and comments
    if (
      trimmed.toLowerCase().startsWith('sideboard') ||
      trimmed.startsWith('//') ||
      trimmed.startsWith('#')
    ) {
      continue;
    }

    // Match patterns:
    // "4 Lightning Bolt" or "4x Lightning Bolt" or "4 Lightning Bolt [M10]"
    const match = trimmed.match(/^(\d+)x?\s+(.+?)(?:\s*\[(\w+)\])?$/i);

    if (match) {
      cards.push({
        quantity: parseInt(match[1], 10),
        name: match[2].trim(),
        setCode: match[3]?.toLowerCase(),
      });
    } else {
      // No quantity prefix - assume 1
      // Try to match name with optional set code
      const nameMatch = trimmed.match(/^(.+?)(?:\s*\[(\w+)\])?$/);
      if (nameMatch && nameMatch[1].trim()) {
        cards.push({
          quantity: 1,
          name: nameMatch[1].trim(),
          setCode: nameMatch[2]?.toLowerCase(),
        });
      }
    }
  }

  return cards;
}

/**
 * Generate MTGO/Arena format text from resolved cards
 */
export function generateDecklistExport(
  mainboard: ResolvedCard[],
  sideboard?: ResolvedCard[]
): string {
  const lines: string[] = [];

  // Mainboard
  for (const card of mainboard) {
    lines.push(`${card.quantity} ${card.name}`);
  }

  // Sideboard (if present)
  if (sideboard && sideboard.length > 0) {
    lines.push('');
    lines.push('Sideboard');
    for (const card of sideboard) {
      lines.push(`${card.quantity} ${card.name}`);
    }
  }

  return lines.join('\n');
}

/**
 * Calculate total deck value from resolved cards
 * Only uses actual backend pricing - never estimates
 */
export function calculateDeckValue(cards: ResolvedCard[]): number | null {
  let total = 0;
  let hasAnyPrice = false;

  for (const card of cards) {
    const cardPrice = card.price?.usd;
    if (cardPrice !== null && cardPrice !== undefined) {
      total += cardPrice * card.quantity;
      hasAnyPrice = true;
    }
    // NEVER estimate missing prices - skip them per CLAUDE.md rules
  }

  return hasAnyPrice ? total : null;
}

/**
 * Group resolved cards by category
 */
export function groupCardsByCategory(
  cards: ResolvedCard[]
): Record<CardTypeCategory, ResolvedCard[]> {
  const groups: Record<CardTypeCategory, ResolvedCard[]> = {
    Creature: [],
    Planeswalker: [],
    Instant: [],
    Sorcery: [],
    Enchantment: [],
    Artifact: [],
    Battle: [],
    Land: [],
    Other: [],
  };

  for (const card of cards) {
    const category = getCardCategory(card.typeLine);
    groups[category].push(card);
  }

  return groups;
}

export const decklistApi = createApi({
  reducerPath: 'decklistApi',
  baseQuery: baseQuery,
  tagTypes: ['DecklistResolve'],
  endpoints: (builder) => ({
    /**
     * Batch resolve card names to full card data (mutation - no caching)
     * Used for CMS validation in DecklistImportModal where caching is not needed
     */
    batchResolveCards: builder.mutation<BatchResolveResponse, BatchResolveRequest>({
      query: (body) => ({
        url: '/cards/batch-resolve',
        method: 'POST',
        body,
        // Public endpoint - no auth required
      }),
      transformResponse: (response: { success: boolean; data: BatchResolveResponse }) => {
        return response.data;
      },
    }),

    /**
     * Batch resolve card names to full card data (query - with caching)
     * Used for public display rendering in Decklist component
     * RTK Query will deduplicate concurrent identical requests and cache results
     */
    getBatchResolvedCards: builder.query<BatchResolveResponse, BatchResolveRequest>({
      query: (body) => ({
        url: '/cards/batch-resolve',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { success: boolean; data: BatchResolveResponse }) => {
        return response.data;
      },
      // Cache for 5 minutes - prices don't change frequently
      keepUnusedDataFor: 300,
    }),
  }),
});

export const { useBatchResolveCardsMutation, useGetBatchResolvedCardsQuery } = decklistApi;
